import { createContext, useLayoutEffect, useRef, useState } from 'react';
import { Box, Menu, pxToRem } from '@pocketlaw/tetris';
import { Falcon } from '@pocketlaw/tetris/macro';
import { usePrevious } from 'react-use';
import { SidebarMenuItem } from '../SidebarMenuItem';
import { useSidebarMenu } from '../useSidebarMenu';
import { GapFix, SidebarMenuCollapsibleChildrenContainer } from './styled';
export const SidebarMenuCollapsibleContext = createContext(null);
export function SidebarMenuCollapsible(props) {
    const { collapsibleKey, open, icon, label, activeChild, children, onToggle } = props;
    const { minimized } = useSidebarMenu();
    const previousMinimized = usePrevious(minimized);
    const childrenContainerRef = useRef(null);
    const [collapsibleOpen, setCollapsibleOpen] = useState((open || activeChild) && !minimized);
    const [menuOpen, setMenuOpen] = useState(false);
    const closeMenu = () => setMenuOpen(false);
    const closeCollapsible = () => setCollapsibleOpen(false);
    const toggleChildren = () => {
        if (minimized) {
            setMenuOpen((prevState) => !prevState);
        }
        else {
            setCollapsibleOpen((prevState) => !prevState);
            onToggle(collapsibleKey);
        }
    };
    const openChildrenContainer = () => {
        if (!childrenContainerRef.current) {
            return;
        }
        const { scrollHeight } = childrenContainerRef.current;
        childrenContainerRef.current.style.maxHeight = pxToRem(scrollHeight);
    };
    const closeChildrenContainer = () => {
        if (!childrenContainerRef.current) {
            return;
        }
        childrenContainerRef.current.style.maxHeight = pxToRem(0);
    };
    const handleChildClick = () => {
        if (minimized) {
            closeMenu();
        }
    };
    useLayoutEffect(() => {
        if (collapsibleOpen) {
            openChildrenContainer();
        }
        else {
            closeChildrenContainer();
        }
    }, [collapsibleOpen, children]);
    /**
     * If collapsible is open and the sidebar gets minimized, close the collapsible.
     */
    useLayoutEffect(() => {
        if (collapsibleOpen && minimized && !previousMinimized) {
            closeChildrenContainer();
            closeCollapsible();
        }
    }, [collapsibleOpen, minimized, previousMinimized]);
    /**
     * If the sidebar goes from minimized to expanded and the collapsible is open, open the children container.
     */
    useLayoutEffect(() => {
        if (!minimized && open) {
            setCollapsibleOpen(true);
        }
    }, [minimized, open]);
    const adornmentRight = collapsibleOpen ? (<Falcon $size={13} icon="chevron-up"/>) : (<Falcon $size={13} icon="chevron-down"/>);
    const value = {
        open: collapsibleOpen,
        mode: minimized ? 'menu' : 'sidebar',
        onChildClick: handleChildClick,
    };
    return (<SidebarMenuCollapsibleContext.Provider value={value}>
      <Box flexDirection="column">
        <Menu.Root open={menuOpen} onClose={closeMenu} target={<SidebarMenuItem icon={icon} label={label} active={activeChild} onClick={toggleChildren} adornmentRight={adornmentRight}/>}>
          {children}
        </Menu.Root>
        <SidebarMenuCollapsibleChildrenContainer ref={childrenContainerRef} $open={collapsibleOpen}>
          <GapFix />
          {children}
        </SidebarMenuCollapsibleChildrenContainer>
      </Box>
    </SidebarMenuCollapsibleContext.Provider>);
}
